/**
 * "Today" chase queue — the my-turn contacts due today, capped at 10, each
 * with the single most important reason it's showing up plus up to two
 * secondary chips. `why.ts` deliberately returns machine codes, not copy —
 * the English mapping lives here in the component layer.
 *
 * Also surfaces a second, independent block: scheduled contacts whose
 * due_date has already arrived ("Scheduled — due now"), fed by
 * `useDueScheduledContacts`/`selectDueScheduledContacts` (Option B — see
 * the plan's DESIGN IT TWICE). That block's fetch is bounded by
 * `DUE_SCHEDULED_FETCH_LIMIT`, a completeness sentinel, not a display cap.
 * Its render is capped at `MAX_QUEUE_ROWS` when collapsed, with a toggle
 * that expands it IN PLACE to the full fetched-and-filtered list — no
 * navigation, no new query. The toggle's label is honest about whether the
 * fetch was provably exhaustive (`isDueScheduledListComplete`): "Show all
 * N due" when it was, the non-numeric "Show more" when the fetch hit its
 * cap and the true count is unknown (it never claims a floor the filtered
 * count can't back up). Incompleteness itself is a fact about the RAW
 * fetch, not the ladder-filtered count — it's surfaced independently via a
 * standalone "Scheduled due list may be incomplete" note that renders
 * whenever the fetch is incomplete, regardless of how many (if any) rows
 * currently pass the ladder, so a 501-row raw fetch that filters down to
 * zero or few valid rows still warns instead of silently reporting "Nothing
 * due today". The SCHEDULED lane grid below
 * excludes exactly the set this block is currently rendering — collapsed or
 * expanded — derived from the same shared `dueScheduledExpanded` boolean
 * and the same `selectDueScheduledQueueContacts` function (see
 * `DashboardSections.tsx`), so the two can never drift apart.
 *
 * Zero paid calls on render: `useTodayQueue`/`useDueScheduledContacts`/
 * `useMarkDone`/`useUpdateContact` are all Supabase-only. The dossier link
 * is the only AI adjacency, and it only routes to the existing
 * human-in-the-loop approval flow — no request fires until the operator
 * clicks through and acts there.
 */

import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LogReplyDialog } from '@/components/crm/LogReplyDialog';
import {
  isDueScheduledListComplete,
  isDueScheduledListDefinitelyIncomplete,
  selectDueScheduledContacts,
  useDueScheduledContacts,
  useMarkDone,
  useTodayQueue,
  useUpdateContact,
} from '@/hooks/crm/useContacts';
import { getChaseReasons, type ChaseReason, type ChaseReasonCode } from '@/lib/crm/why';
import { formatUiDate } from '@/lib/ui-format';
import type { Contact } from '@/lib/crm/types';

const MAX_QUEUE_ROWS = 10;
const MAX_SECONDARY_REASONS = 2;
const SNOOZE_DAYS = 7;

const CHASE_REASON_COPY: Record<ChaseReasonCode, (reason: ChaseReason) => string> = {
  dropped_inbound: () => 'They replied — you owe a response',
  overdue: (reason) => {
    const days = reason.days ?? 0;
    return `Overdue by ${days} ${days === 1 ? 'day' : 'days'}`;
  },
  due_today: () => 'Due today',
  waiting_too_long: (reason) => `Waiting ${reason.days ?? 0} days for a reply`,
  no_next_action: () => 'No next action recorded',
  no_due_date: () => 'No due date set',
};

function describeChaseReason(reason: ChaseReason): string {
  return CHASE_REASON_COPY[reason.code](reason);
}

/**
 * Label for the due-scheduled expand/collapse toggle. A named helper rather
 * than a nested ternary (`src/CLAUDE.md` forbids nested ternary chains).
 */
function dueScheduledToggleLabel(
  expanded: boolean,
  isComplete: boolean,
  exactCount: number,
): string {
  if (expanded) return 'Show fewer';
  if (isComplete) return `Show all ${exactCount} due`;
  return 'Show more';
}

function toIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Adds `SNOOZE_DAYS` to the contact's current due date (or `now` if unset/invalid). */
function snoozedDueDate(contact: Contact, now: Date): string {
  const parsed = contact.due_date ? new Date(contact.due_date) : now;
  const anchor = Number.isNaN(parsed.getTime()) ? now : parsed;
  const next = new Date(anchor);
  next.setDate(next.getDate() + SNOOZE_DAYS);
  return toIsoDate(next);
}

function dossierHrefFor(contact: Contact): string | null {
  return contact.external_source === 'heyreach' && contact.external_contact_id
    ? `/leads/${encodeURIComponent(contact.external_contact_id)}`
    : null;
}

/**
 * The contacts actually rendered in the Today band, in order — the same
 * `MAX_QUEUE_ROWS` cap this component applies to `useTodayQueue`'s data.
 * Exported so `DashboardSections` can exclude exactly these contacts (not
 * the full, uncapped due-today set) from the MY TURN lane list below,
 * keeping each person visible exactly once instead of twice (see
 * `DashboardSections.tsx`).
 */
export function selectTodayQueueContacts(contacts: Contact[]): Contact[] {
  return contacts.slice(0, MAX_QUEUE_ROWS);
}

/**
 * The exact, ladder-filtered due-scheduled set (`selectDueScheduledContacts`'s
 * output) the Today block is CURRENTLY rendering — sliced to `MAX_QUEUE_ROWS`
 * when collapsed, or returned in full when `expanded` (the operator toggled
 * "Show all N due"). Exported so `DashboardSections` can exclude exactly this
 * set from the SCHEDULED grid below, driven by the SAME `expanded` boolean
 * passed down from `src/pages/crm/index.tsx` — the two surfaces derive their
 * shown/excluded sets from one shared function and one shared boolean, so
 * they can never independently disagree about which contacts are visible
 * where.
 */
export function selectDueScheduledQueueContacts(contacts: Contact[], expanded: boolean): Contact[] {
  return expanded ? contacts : contacts.slice(0, MAX_QUEUE_ROWS);
}

export interface TodayQueueProps {
  /**
   * A single shared "now" snapshot, taken once by `src/pages/crm/index.tsx`
   * (the common ancestor of this component and `DashboardSections`) and
   * threaded down as a prop — not computed independently here. Both
   * components call `useDueScheduledContacts`, and each hook invocation
   * used to compute its own `new Date()` for the query key; if those two
   * renders straddled local midnight, they could disagree on which day's
   * cached result to serve, silently dropping a newly-due contact from
   * both surfaces. Passing the identical value to both eliminates that
   * race — see `useDueScheduledContacts`'s own doc comment.
   */
  now: Date;
  /**
   * Whether the "Scheduled — due now" block is currently showing its full
   * fetched-and-filtered list instead of the `MAX_QUEUE_ROWS`-capped
   * default. Lifted to `src/pages/crm/index.tsx` (the common ancestor of
   * this component and `DashboardSections`) rather than kept as local
   * state here, so both components derive the IDENTICAL shown/excluded id
   * set from the IDENTICAL boolean via `selectDueScheduledQueueContacts` —
   * the invariant three prior adversarial review rounds converged on after
   * finding that two independently-reimplemented "what's currently shown"
   * sets can drift apart.
   */
  dueScheduledExpanded: boolean;
  /**
   * Toggles `dueScheduledExpanded` in the shared ancestor. Passed down
   * rather than managed as local state here for the same reason —
   * `DashboardSections` must react to the identical toggle, in the same
   * render, not a locally-scoped one it can't see.
   */
  onToggleDueScheduledExpanded: () => void;
}

export function TodayQueue({
  now,
  dueScheduledExpanded,
  onToggleDueScheduledExpanded,
}: TodayQueueProps): JSX.Element {
  const { data: contacts = [], isLoading, isError, refetch } = useTodayQueue();
  const dueScheduledQuery = useDueScheduledContacts(now);
  const markDone = useMarkDone();
  const updateContact = useUpdateContact();
  const [replyingTo, setReplyingTo] = useState<Contact | null>(null);
  const queueContacts = selectTodayQueueContacts(contacts);
  const dueScheduledContacts = selectDueScheduledContacts(dueScheduledQuery, now);
  const dueScheduledShown = selectDueScheduledQueueContacts(dueScheduledContacts, dueScheduledExpanded);
  // Independent of the current expand state — "is there anything to
  // toggle", not "is something currently hidden". The toggle affordance
  // must render in EITHER expanded or collapsed state, so the operator can
  // always collapse back.
  const hasMoreDueScheduled = dueScheduledContacts.length > MAX_QUEUE_ROWS;
  const dueScheduledLoading = dueScheduledQuery.isLoading;
  const dueScheduledError = dueScheduledQuery.isError;
  // A fact about the RAW fetch, independent of how many rows survive the
  // ladder filter — must be read directly, never derived from
  // `hasMoreDueScheduled` (the filtered count), or a 501-row raw fetch that
  // filters down to few/zero valid rows would silently report as complete.
  const dueScheduledComplete = isDueScheduledListComplete({ data: dueScheduledQuery.data });
  // NOT `!dueScheduledComplete` (F11): that negation is also `true` before
  // the fetch has resolved at all (`data === undefined`), which would show
  // the "may be incomplete" warning for a query that hasn't run yet. This is
  // `true` only once a successful fetch has PROVEN the cap was hit.
  const dueScheduledDefinitelyIncomplete = isDueScheduledListDefinitelyIncomplete({
    data: dueScheduledQuery.data,
    isSuccess: dueScheduledQuery.isSuccess,
  });

  // Re-derived from BOTH lists: only true once my-turn AND due-scheduled
  // have both resolved (no longer loading, no error) and both came back
  // empty — otherwise this could say "nothing due" directly above a
  // visible due-scheduled contact, or while either query's own error
  // state is what should be showing instead. Also requires the due-scheduled
  // fetch to be known-complete: if the raw fetch hit its cap, we don't
  // actually know nothing is due, so "Nothing due today" must not render —
  // the incompleteness note takes precedence instead.
  const showNothingDueToday =
    !isLoading
    && !isError
    && !dueScheduledLoading
    && !dueScheduledError
    && queueContacts.length === 0
    && dueScheduledShown.length === 0
    && dueScheduledComplete;

  const handleMarkDone = (contactId: string): void => {
    markDone.mutate(contactId);
  };

  const handleSnooze = (contact: Contact): void => {
    updateContact.mutate({
      id: contact.id,
      updates: { due_date: snoozedDueDate(contact, now) },
      originalContact: contact,
    });
  };

  return (
    <section aria-labelledby="today-queue-heading" className="mb-8">
      <h2 id="today-queue-heading" className="mb-4 text-lg font-semibold tracking-tight text-foreground">
        Today
      </h2>

      {isLoading && (
        <div
          className="rounded-lg border border-border bg-surface px-4 py-8 text-center"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm text-muted-foreground">Loading today&apos;s queue...</p>
        </div>
      )}

      {!isLoading && isError && (
        <div
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-6 text-center"
          role="alert"
        >
          <p className="text-sm text-muted-foreground">Today&apos;s queue could not be loaded.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 min-h-11 md:min-h-11"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {showNothingDueToday && (
        <div
          className="rounded-lg border-2 border-dashed border-border bg-surface px-4 py-8 text-center"
          role="status"
        >
          <p className="text-sm text-muted-foreground">Nothing due today.</p>
        </div>
      )}

      {!isLoading && !isError && queueContacts.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {queueContacts.map((contact) => {
            const [primary, ...rest] = getChaseReasons(contact, now);
            const secondary = rest.slice(0, MAX_SECONDARY_REASONS);
            const dossierHref = dossierHrefFor(contact);
            // Distinct DOM ids from `ContactCard`'s (`crm-contact-name-*`) —
            // a given contact only ever renders in one place at a time (see
            // the MY TURN exclusion in `DashboardSections.tsx`), but keeping
            // the id namespaces separate avoids any accidental collision.
            const contactNameId = `today-queue-contact-name-${contact.id}`;
            const openContactLabelId = `today-queue-open-contact-label-${contact.id}`;

            return (
              <li
                key={contact.id}
                className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/crm/contact/${encodeURIComponent(contact.id)}`}
                    className="rounded font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-labelledby={`${openContactLabelId} ${contactNameId}`}
                  >
                    <span id={openContactLabelId} className="sr-only">
                      Open contact
                    </span>
                    <span id={contactNameId} data-content-origin="external">
                      {contact.name}
                    </span>
                  </Link>
                  {contact.company && (
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Building2 className="h-3 w-3" aria-hidden="true" />
                      <span data-content-origin="external">{contact.company}</span>
                    </div>
                  )}
                  {primary && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {describeChaseReason(primary)}
                    </p>
                  )}
                  {secondary.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {secondary.map((reason) => (
                        <Badge
                          key={reason.code}
                          variant="outline"
                          className="text-xs font-normal text-muted-foreground"
                        >
                          {describeChaseReason(reason)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/*
                    First action on purpose. The most common reason a contact is
                    sitting here is that the operator already answered outside
                    Tacit and nothing recorded it.
                  */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 md:min-h-11"
                    onClick={() => setReplyingTo(contact)}
                  >
                    I replied
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 md:min-h-11"
                    disabled={markDone.isPending}
                    onClick={() => handleMarkDone(contact.id)}
                  >
                    Mark done
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 md:min-h-11"
                    disabled={updateContact.isPending}
                    onClick={() => handleSnooze(contact)}
                  >
                    Snooze 1 week
                  </Button>
                  {dossierHref && (
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="min-h-11 md:min-h-11"
                    >
                      <Link to={dossierHref}>Open conversation dossier</Link>
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {(dueScheduledLoading || dueScheduledError || dueScheduledShown.length > 0 || dueScheduledDefinitelyIncomplete) && (
        <h3
          id="today-due-scheduled-heading"
          className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Scheduled — due now
        </h3>
      )}

      {dueScheduledLoading && (
        <div
          className="rounded-lg border border-border bg-surface px-4 py-6 text-center"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm text-muted-foreground">Loading scheduled contacts due now...</p>
        </div>
      )}

      {!dueScheduledLoading && dueScheduledError && (
        <div
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-6 text-center"
          role="alert"
        >
          <p className="text-sm text-muted-foreground">Scheduled contacts due now could not be loaded.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 min-h-11 md:min-h-11"
            onClick={() => void dueScheduledQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {!dueScheduledLoading && !dueScheduledError && dueScheduledShown.length > 0 && (
        <ul
          aria-labelledby="today-due-scheduled-heading"
          className="divide-y divide-border rounded-lg border border-border bg-card"
        >
          {dueScheduledShown.map((contact) => {
            const contactNameId = `today-due-scheduled-contact-name-${contact.id}`;
            const openContactLabelId = `today-due-scheduled-open-contact-label-${contact.id}`;

            return (
              <li
                key={contact.id}
                className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/crm/contact/${encodeURIComponent(contact.id)}`}
                    className="rounded font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-labelledby={`${openContactLabelId} ${contactNameId}`}
                  >
                    <span id={openContactLabelId} className="sr-only">
                      Open contact
                    </span>
                    <span id={contactNameId} data-content-origin="external">
                      {contact.name}
                    </span>
                  </Link>
                  {contact.company && (
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Building2 className="h-3 w-3" aria-hidden="true" />
                      <span data-content-origin="external">{contact.company}</span>
                    </div>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  Due {formatUiDate(contact.due_date)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {!dueScheduledLoading && !dueScheduledError && dueScheduledDefinitelyIncomplete && (
        <p className="mt-2 text-xs text-muted-foreground" role="status">
          Scheduled due list may be incomplete.
        </p>
      )}

      {!dueScheduledLoading && !dueScheduledError && hasMoreDueScheduled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2"
          onClick={onToggleDueScheduledExpanded}
        >
          {dueScheduledToggleLabel(
            dueScheduledExpanded,
            dueScheduledComplete,
            dueScheduledContacts.length,
          )}
        </Button>
      )}

      {replyingTo && (
        <LogReplyDialog
          key={replyingTo.id}
          contact={replyingTo}
          onClose={() => setReplyingTo(null)}
        />
      )}
    </section>
  );
}
